<template>
  <div class="request-details-info">
    <div class="request-details-info__main">
      <div class="request-details-info__fields">
        <div
          v-for="(row, rowIndex) in fieldRows"
          :key="row.title || rowIndex"
          class="request-details-info__group"
        >
          <div
            v-if="row.title"
            class="request-details-info__group-title"
          >
            {{ row.title }}
          </div>

          <fields-renderer
            :config="{ columnsCount: row.columnsCount, fields: row.fields }"
            :form-data="formData"
            :fields-config="fieldsConfig"
            :validation-errors="validationErrors"
            :initial-autocompletes="initialAutocompletes"
            :permissions="permissions"
            :edit="edit"
            :create-mode="createMode"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
            @unlock-fields-on-dbl-click="unlockFieldsOnDblClick"
          />
        </div>
      </div>

      <!--    A fixed height, whatever the form beside it comes to - see the styles
              below.-->
      <div class="request-details-info__side">
        <div class="request-details-info__side-inner">
          <task-paths-map-panel
            :streets="streets"
          />
        </div>
      </div>
    </div>

    <div
      v-if="logs.length"
      class="request-details-info__log"
    >
      <div class="request-details-info__group-title">
        {{ translate(`${NS}.log_history`) }}
      </div>

      <div class="request-details-info__log-box">
        <div
          v-for="log in logs"
          :key="log.id"
          class="request-details-info__log-row"
        >
          <span>{{ logLabel(log) }}</span>
          <span>{{ log.user }}</span>
          <span>{{ formatTimestamp(log.created_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
// The map draws the streets of the task behind the request, which is the very thing the
// task screens draw - so it is the same panel rather than a second one like it.
import TaskPathsMapPanel from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskPathsMapPanel.vue'
import { toTaskSnapshotForm } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/requestData'
import { useRequestTask } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useRequestTask'
import { useRequestAbnormality } from '@/modules/road-maintenance-module/winter-service-v2/requests/composables/useRequestAbnormality'
import { useTranslate } from '@/global/composables/useTranslate'
import { dateService, FORMATS } from '@/global/services/date'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { TASKS_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'
import {
  buildRequestLogsEndpoint,
  buildRequestStreetsEndpoint,
  REQUESTS_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/requests/config/constants'

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its `rows` hold the grouped
  // field lists (see useRequestBasicInfo for why they live there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits([
  'set-edit'
])

const { translate } = useTranslate()
const { setRecord } = useRequestAbnormality()
const { setRequestTask, clearRequestTask } = useRequestTask()

const streets = ref([])
const logs = ref([])

const fieldRows = computed(() => props.config?.rows || [])

// FieldsRenderer only reports the double-click; turning the tab into edit mode is the
// block's job, and Content.vue is what listens for `set-edit`. Only the vehicle and the
// driver can bring the screen out of read-only this way: FieldsRenderer swallows the
// double-click on a field of its own that is not `editable`, which every other field
// here is not - see useRequestFields.
const unlockFieldsOnDblClick = () => {
  if (props.createMode || (props.permissions && !props.permissions.canEdit)) {
    return
  }

  emit('set-edit', true)
}

// The log's kind is a codebook entry, whose description is a translation key.
const logLabel = (log) => log?.type?.description
  ? translate(log.type.description)
  : log?.type?.name || ''

const formatTimestamp = (value) => value
  ? dateService.formatCustom(value, FORMATS.isoDatetime, FORMATS.datetimeShort)
  : ''

/**
 * Almost every field on this screen belongs to the task the request was made from, and
 * the request endpoint reports only the task's id - so the task is fetched here and its
 * values are merged into the form. None of them is `creatable`, so none is ever sent
 * back with a save.
 *
 * The vehicle is the exception: the request may take one other than the task's, and what
 * the vehicle decides has to follow the one that actually takes it - so the request's own
 * is handed over rather than left to the task's.
 */
const loadTaskSnapshot = async (taskId) => {
  if (!taskId) {
    return
  }

  try {
    const { data } = await api(WINTER_SERVICE_MODULE).get(`${TASKS_ENDPOINT}/${taskId}`)

    // Kept as well as merged: a save replaces the form with the request alone, and
    // everything the task contributed has to be folded back in (see `afterSave` in
    // useRequestBasicInfo).
    setRequestTask(data)
    props.updateData(toTaskSnapshotForm(data, props.formData?.vehicle))
  }
  catch (exception) {
    console.error(exception)
  }
}

/**
 * The streets the map draws. This endpoint keeps them under a `data` key of its own,
 * alongside the totals, so the payload sits one level deeper than on a plain listing.
 */
const loadStreets = async (requestId) => {
  if (!requestId) {
    return
  }

  try {
    const { data } = await api(WINTER_SERVICE_MODULE).get(buildRequestStreetsEndpoint(requestId))

    if (isArray(data)) {
      streets.value = data
      return
    }

    streets.value = isArray(data?.data) ? data.data : []
  }
  catch (exception) {
    console.error(exception)
    streets.value = []
  }
}

const loadLogs = async (requestId) => {
  if (!requestId) {
    return
  }

  try {
    const { data } = await api(WINTER_SERVICE_MODULE).get(buildRequestLogsEndpoint(requestId))
    logs.value = isArray(data) ? data : []
  }
  catch (exception) {
    console.error(exception)
    logs.value = []
  }
}

// Driven off the record rather than off `onMounted`: the tab fetches the request itself,
// and until that lands there is no task id and no request id to ask about.
watch(() => props.formData?.id, (requestId) => {
  if (!requestId) {
    return
  }

  // What the field list frames red. It is read from the record rather than from the
  // form, because the form flattens the vehicle and the driver down to their ids.
  setRecord(props.formData)

  loadStreets(requestId)
  loadLogs(requestId)
}, { immediate: true })

watch(() => props.formData?.task_id ?? props.formData?.foreign_id, (taskId) => {
  loadTaskSnapshot(taskId)
}, { immediate: true })

// The task is shared state, so it does not outlive the screen that filled it in.
onUnmounted(clearRequestTask)
</script>

<style scoped>
.request-details-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
}

.request-details-info__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  /* The map keeps its own height, so the row must not stretch its cell to the form's -
     see the side column below. */
  align-items: start;
}

.request-details-info__fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.request-details-info__group {
  padding: 12px 20px 4px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

.request-details-info__group-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

/* A fixed height rather than however tall the form beside it happens to be - the form
   grows and shrinks with what the request carries, and a map that follows it is a
   different map on every request. Same height as the task screens - see
   TaskDetailsInfo. */
.request-details-info__side {
  min-width: 0;
  height: 480px;
}

.request-details-info__side-inner {
  height: 100%;
}

.request-details-info__log-box {
  padding: 12px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

/* A grid rather than a flex row, so the user and the timestamp of every entry line up
   under each other however long the labels are. */
.request-details-info__log-row {
  display: grid;
  grid-template-columns: max-content max-content max-content;
  column-gap: 24px;
  padding-bottom: 8px;
  font-size: 13px;
  color: #374151;
}

/* Stacked under the form, and shorter: the map has the full width to itself here. */
@media (max-width: 960px) {
  .request-details-info__main {
    grid-template-columns: minmax(0, 1fr);
  }

  .request-details-info__side {
    height: 320px;
  }
}
</style>

<style lang="scss">
/* Unscoped: the border belongs to Vuetify's own fieldset inside the field component,
   which a scoped rule from here could never reach. The class is put on the vehicle or
   the driver field when the picked one can no longer take the request - it lands on the
   field component's root element, and the fieldset sits inside it.
   The class is doubled on purpose: AutocompleteField paints that very fieldset grey
   from a scoped `::v-deep .v-text-field--outlined fieldset` rule of its own, which is
   already `!important` and, with the scope attribute, more specific than a single class
   here - so a plainer selector loses however loud it is. */
.request-field--abnormal.request-field--abnormal {
  .v-text-field--outlined fieldset {
    border-color: #EF4444 !important;
    border-width: 2px !important;
  }
}
</style>
